import apiClient, { ApiError } from './apiClient';

export type BecasDossierStatus =
  | 'profile_incomplete'
  | 'documents_incomplete'
  | 'ready_to_pay'
  | 'paid'
  | 'submitted';

export type BecasPayProvider = 'stripe' | 'transbank' | 'free';

export interface BecasProfile {
  first_name: string;
  last_name: string;
  birth_date: string;
  nationality: string;
  position: string;
  photo_url?: string;
  academic_level: string;
  graduation_year: number | string;
  gpa?: number | string;
  english_level: string;
  reel_url?: string;
  motivation: string;
  guardian_name?: string;
  guardian_phone?: string;
  guardian_email?: string;
}

export interface BecasDocuments {
  transcript_file_id?: string;
  transcript_url?: string;
  transcript_file_name?: string;
}

export interface BecasPaymentInfo {
  amount?: number;
  currency?: string;
  locale?: string;
  plan_name?: string;
  plan_id?: string;
  original_amount?: number;
  discount_percent?: number;
  provider?: BecasPayProvider;
  publishable_key?: string;
}

export interface BecasDossier {
  status: BecasDossierStatus;
  profile?: Partial<BecasProfile> | null;
  documents?: BecasDocuments | null;
  payment?: BecasPaymentInfo | null;
  publishable_key?: string;
  missing_fields?: string[];
  missing_docs?: string[];
}

export interface BecasPayRequest {
  provider: BecasPayProvider;
  payment_token?: string;
  purchase_id?: string;
  receipt_data?: string;
  discount_code?: string;
}

export interface UploadedFile {
  id: string;
  url: string;
  file_name?: string;
}

const EMPTY_DOSSIER: BecasDossier = { status: 'profile_incomplete' };

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object';
}

function isDossierStatus(value: unknown): value is BecasDossierStatus {
  return (
    value === 'profile_incomplete' ||
    value === 'documents_incomplete' ||
    value === 'ready_to_pay' ||
    value === 'paid' ||
    value === 'submitted'
  );
}

function asString(value: unknown): string | undefined {
  if (typeof value === 'string' && value) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
}

function extractProfile(raw: Record<string, unknown>): Partial<BecasProfile> {
  const nested = isRecord(raw.profile) ? raw.profile : raw;
  return {
    first_name: asString(nested.first_name) ?? '',
    last_name: asString(nested.last_name) ?? '',
    birth_date: asString(nested.birth_date) ?? '',
    nationality: asString(nested.nationality) ?? '',
    position: asString(nested.position) ?? '',
    photo_url: asString(nested.photo_url) ?? '',
    academic_level: asString(nested.academic_level) ?? '',
    graduation_year: (nested.graduation_year as number | string) ?? '',
    gpa: (nested.gpa as number | string) ?? '',
    english_level: asString(nested.english_level) ?? '',
    reel_url: asString(nested.reel_url) ?? '',
    motivation: asString(nested.motivation) ?? '',
    guardian_name: asString(nested.guardian_name) ?? '',
    guardian_phone: asString(nested.guardian_phone) ?? '',
    guardian_email: asString(nested.guardian_email) ?? '',
  };
}

function extractDocuments(raw: Record<string, unknown>): BecasDocuments | null {
  const list = Array.isArray(raw.documents) ? raw.documents : [];
  const transcript = list.find((item) => isRecord(item) && item.kind === 'academic_transcript');
  const rec = isRecord(transcript)
    ? transcript
    : isRecord(raw.documents) && !Array.isArray(raw.documents)
      ? raw.documents
      : null;
  if (!rec) return null;
  const file = isRecord(rec.file) ? rec.file : null;
  const fileId = asString(rec.file_id) ?? asString(file?.id) ?? asString(rec.transcript_file_id);
  if (!fileId) return null;
  return {
    transcript_file_id: fileId,
    transcript_url: asString(file?.url) ?? asString(rec.transcript_url),
    transcript_file_name: asString(file?.file_name) ?? asString(rec.transcript_file_name),
  };
}

function extractPayment(raw: Record<string, unknown>): BecasPaymentInfo | null {
  const nested = isRecord(raw.payment) ? raw.payment : null;
  const provider = asString(nested?.provider) ?? asString(raw.payment_provider);
  const publishableKey = asString(nested?.publishable_key) ?? asString(raw.publishable_key);
  if (!nested && !provider && !publishableKey) return null;
  return {
    ...(nested as BecasPaymentInfo | null),
    provider: provider as BecasPayProvider | undefined,
    publishable_key: publishableKey,
  };
}

export function normalizeDossier(raw: unknown): BecasDossier {
  if (!isRecord(raw)) return EMPTY_DOSSIER;
  const nested = isRecord(raw.dossier) ? raw.dossier : raw;
  const status = isDossierStatus(nested.status) ? nested.status : 'profile_incomplete';
  const missingFields = Array.isArray(nested.missing_fields)
    ? nested.missing_fields.filter((item): item is string => typeof item === 'string')
    : undefined;
  const missingDocs = Array.isArray(nested.missing_docs)
    ? nested.missing_docs.filter((item): item is string => typeof item === 'string')
    : undefined;
  return {
    status,
    profile: extractProfile(nested),
    documents: extractDocuments(nested),
    payment: extractPayment(nested),
    publishable_key: asString(nested.publishable_key) ?? extractPayment(nested)?.publishable_key,
    missing_fields: missingFields,
    missing_docs: missingDocs,
  };
}

export const becasApiService = {
  async getDossier(): Promise<BecasDossier> {
    try {
      const { data } = await apiClient.get<unknown>('/becas/dossier');
      return normalizeDossier(data);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 204)) {
        return EMPTY_DOSSIER;
      }
      throw err;
    }
  },

  async saveProfile(profile: BecasProfile): Promise<BecasDossier> {
    const { data } = await apiClient.put<unknown>('/becas/dossier/profile', profile);
    return normalizeDossier(data);
  },

  async saveDocuments(fileId: string): Promise<BecasDossier> {
    const { data } = await apiClient.post<unknown>('/becas/dossier/documents', {
      kind: 'academic_transcript',
      file_id: fileId,
    });
    return normalizeDossier(data);
  },

  async pay(payload: BecasPayRequest): Promise<BecasDossier> {
    const body: Record<string, string> = { provider: payload.provider };
    if (payload.payment_token) body.payment_token = payload.payment_token;
    if (payload.purchase_id) body.purchase_id = payload.purchase_id;
    if (payload.receipt_data) body.receipt_data = payload.receipt_data;
    if (payload.discount_code) body.discount_code = payload.discount_code;
    const { data } = await apiClient.post<unknown>('/becas/pay', body);
    return normalizeDossier(data);
  },

  async submit(): Promise<BecasDossier> {
    const { data } = await apiClient.post<unknown>('/becas/submit', {});
    return normalizeDossier(data);
  },
};

export default becasApiService;
